import { useImages } from "@features/images/use-images";
import { useLoras } from "@features/loras/use-loras";
import { useWebSockets } from "@features/use-socket";
import { Spinner } from "@radix-ui/themes";
import { Billing, BillingApple } from "@views/app/account/billing";
import { MyModels } from "@views/app/account/my-models";
import { MyPresets } from "@views/app/account/my-presets";
import { Camera } from "@views/app/camera";
import { Home } from "@views/app/home";
import { CameraRollPage } from "@views/app/home/all";
import { Lora } from "@views/app/lora/create";
import { ProjectPage } from "@views/app/projects";
import { ProjectFormPage } from "@views/app/projects/form";
import { Studio } from "@views/app/studio";
import { TemplatesPage } from "@views/app/templates";
import { KeyboardPage } from "@views/keyboard";
import { LayoutRouter } from "@views/layout/layout";
import SignInPage from "@views/login/login";
import { Logout } from "@views/login/logout";
import PlanPage from "@views/login/plan";
import { UploadIntro } from "@views/welcome/upload-intro";
import { Welcome } from "@views/welcome/welcome";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useUser, useUserRefreshFromApp } from "./features/user/use-user";

export const Router = () => {
  const { user } = useUser();
  useWebSockets();
  useUserRefreshFromApp();

  if (user.isLoading) {
    return (
      <div className="bg-slate-950 flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  const planChange =
    user.data &&
    (!user.data.plan ||
      new URLSearchParams(window.location.search).get("plan"));

  return (
    <BrowserRouter>
      <Routes>
        <Route path="keyboard" element={<KeyboardPage />} />
        {user.data && !planChange && (
          <>
            <Route path="/:site/" element={<LayoutRouter redirect />}>
              <Route path="project/:id" element={<ProjectPage />} />
              <Route path="project/:id/form" element={<ProjectFormPage />} />
              <Route path="project/:id/trash" element={<ProjectPage trash />} />
              <Route path="project/:id/search" element={<ProjectPage />} />
              <Route path="trash" element={<CameraRollPage trash />} />
              <Route path="home/search" element={<CameraRollPage />} />
              <Route path="home/search/:q" element={<CameraRollPage />} />
              <Route path="lora/:id" element={<Lora />} />
              <Route path="lora/:id/:step" element={<Lora />} />
              <Route path="camera/:id" element={<Camera />} />
              <Route path="studio/:id" element={<Studio />} />
              <Route path="templates" element={<TemplatesPage />} />

              <Route path="welcome" element={<Welcome />} />
              <Route path="billing" element={<Billing />} />
              <Route path="billing/apple" element={<BillingApple />} />
              <Route path="my-models" element={<MyModels />} />
              <Route path="my-presets" element={<MyPresets />} />
              <Route path="logout" element={<Logout />} />
              <Route path="home" element={<HomeOrWelcome />} />
              <Route path="*" element={<HomeOrWelcome />} />
              <Route path="" element={<HomeOrWelcome />} />
            </Route>

            <Route element={<LayoutRouter />}>
              <Route path="welcome" element={<Welcome />} />
              <Route path="billing" element={<Billing />} />
              <Route path="billing/apple" element={<BillingApple />} />
              <Route path="my-models" element={<MyModels />} />
              <Route path="my-presets" element={<MyPresets />} />
              <Route path="logout" element={<Logout />} />
              <Route
                path="*"
                element={
                  <Navigate
                    to={
                      user.data?.credits_used === 0 ? "/upload" : "/portraits/"
                    }
                  />
                }
              />
              <Route
                path="/"
                element={
                  <Navigate
                    to={
                      user.data?.credits_used === 0 ? "/upload" : "/portraits/"
                    }
                  />
                }
              />
            </Route>
          </>
        )}
        {user.data && planChange && (
          <>
            <Route path="/" element={<PlanPage />} />
            <Route path="logout" element={<Logout />} />
          </>
        )}
        <Route path="/upload" element={<UploadIntro />} />
        {!user.data && (
          <>
            <Route path="*" element={<SignInPage />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
};

// Session storage key to track if user has dismissed upload page
const UPLOAD_DISMISSED_KEY = "upload_dismissed";

export const markUploadDismissed = () => {
  sessionStorage.setItem(UPLOAD_DISMISSED_KEY, "true");
};

const hasUploadBeenDismissed = () => {
  return sessionStorage.getItem(UPLOAD_DISMISSED_KEY) === "true";
};

const HomeOrWelcome = () => {
  const { user } = useUser();
  const { images } = useImages();
  const { loras } = useLoras();

  // State to track if we should show welcome page
  const hasPhotos = (images.data?.length || 0) > 0;
  const isNewUser =
    !hasPhotos && !loras.data?.length && user.data?.credits_used === 0;

  // Only redirect to upload if new user AND hasn't dismissed the upload page
  const showUpload = isNewUser && !hasUploadBeenDismissed();

  if (images.isLoading || loras.isLoading) {
    return (
      <div className="bg-slate-950 flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (showUpload) {
    return <Navigate to={"/upload" + document.location.search} />;
  }

  return <Home />;
};
